import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAssetMeta, lifecycleTone, LIFECYCLE_LABEL, holderLabel, tagText } from '../lib/assets';
import { useConfirm } from '../components/ConfirmDialog';
import { CategoryIcon, pinStatus, RING_LEGEND } from '../components/MapPin';
import BuildingView from '../components/BuildingView';
import StoragePanel from '../components/StoragePanel';
import { DeskPin, DeskList, deskTone } from '../components/DeskMap';
import { labelBoxOf } from '../lib/polylabel';
import '../components/Assets.css';

/**
 * The map: a place's floor plan with its devices pinned on it (092), the
 * rooms beneath it named on it (098), and the rooms outlined on it (099).
 *
 * A pin is two fractions of the picture, so it stays put whatever size the
 * plan is drawn at. IT uploads a plan per place (a floor, a room - whatever
 * level of the tree the picture shows), then places each device by
 * clicking "Place" on it and clicking the plan; a placed pin is dragged to
 * move it. Everybody who may see the place sees the pins and the labels;
 * placing needs write on the device, a plan and its labels need write on
 * the place.
 *
 * The devices listed are those at the place AND at the places beneath it,
 * grouped by room: a floor's plan is where the devices of its rooms are
 * pinned. A device has one pin, on the plan of the nearest place above it
 * that has one, so a device in a room with a plan of its own is listed
 * with a link to that plan rather than placed here.
 *
 * "Label a room" names a room beneath the place (or any text): just the
 * name at a click, or the room's outline - a rectangle dragged, or its
 * corners clicked - with the name fitted inside it. A label is dragged to
 * move it (an outlined room moves as a whole) and removed from its ×.
 *
 * A ROOM'S MAP CUT FROM THE FLOOR'S. A room with no plan of its own that is
 * outlined on the floor's plan opens as that part of the floor's picture,
 * enlarged (`crop` from the server). Nothing is cut or stored: the page
 * shows the whole picture moved and scaled so the outline fills the frame,
 * and the pins are the floor's pins, drawn in the crop's coordinates.
 * Coordinates are therefore converted at one place: `toView` for drawing,
 * `fromView` for what is sent.
 *
 * A PIN is an icon for the device's category inside a ring for its state
 * (components/MapPin.js: green in use, amber not seen for 30 days, red
 * faulty or retired, grey not in use), with the tag shown on hover. A
 * legend under the plan says which is which; "Show retired" adds retired
 * devices, which are otherwise left off the map.
 *
 * EDITING OUTLINES. "Edit outlines" puts handles on every outline: a corner
 * is dragged to reshape it (a rectangle stays a rectangle), a rectangle's
 * side is dragged to move just that side. "Tidy with AI" asks Claude to
 * fit the outlines to the walls on the plan (lib/map-outline-ai.js); its
 * answers are drawn dashed over the outlines as PROPOSALS, IT ticks the
 * ones to keep, and only "Apply" saves them, one ordinary PATCH each.
 */
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
// Below 100% the whole of a large floor fits on the screen; above, the plan scrolls in its frame.
const ZOOMS = [0.5, 0.75, 1, 1.5, 2, 3];
/** How far the pointer must move, in screen pixels, before a press is a drag. */
const DRAG_PX = 5;
const WHOLE = { x: 0, y: 0, w: 1, h: 1 };

const ago = (t) => {
  if (!t) return null;
  const d = Math.round((Date.now() - new Date(t).getTime()) / 86400000);
  return d < 1 ? 'today' : `${d} day${d === 1 ? '' : 's'} ago`;
};

/** The devices in order, cut into groups by the place they are at: this place first, then each room. */
const byRoom = (devices, placeId) => {
  const groups = [];
  devices.forEach((d) => {
    const key = d.location_id || placeId;
    let g = groups.find((x) => x.key === key);
    if (!g) {
      g = { key, here: key === placeId, name: d.location_name, devices: [] };
      groups.push(g);
    }
    g.devices.push(d);
  });
  return groups;
};

/** The box around an outline's corners, as fractions. */
const boxOf = (pts) => {
  const xs = pts.map((p) => Number(p[0]));
  const ys = pts.map((p) => Number(p[1]));
  const x = Math.min(...xs);
  const y = Math.min(...ys);
  return { x, y, w: Math.max(...xs) - x, h: Math.max(...ys) - y };
};
/** An outline's size, as a fraction of the picture: the shoelace formula (a rectangle's is w × h). */
const areaOf = (pts) => Math.abs(pts.reduce((t, p, i) => {
  const q = pts[(i + 1) % pts.length];
  return t + Number(p[0]) * Number(q[1]) - Number(q[0]) * Number(p[1]);
}, 0)) / 2;
const clamp01 = (v) => Math.min(1, Math.max(0, v));
/** Four corners whose edges are all level or upright: a rectangle, kept one when resized. */
const isRect = (pts) => pts.length === 4 && pts.every((p, i) => {
  const q = pts[(i + 1) % 4];
  return Number(p[0]) === Number(q[0]) || Number(p[1]) === Number(q[1]);
});
/**
 * The box an outline's name goes in: a rectangle's own; any other shape's sits
 * inside it, at the point farthest from its edges (lib/polylabel), so the name
 * of a U or an L is never over the rooms in its gap.
 */
const nameBoxOf = (pts) => (isRect(pts) ? boxOf(pts) : labelBoxOf(pts));
const rectFrom = (x0, y0, x1, y1) => {
  const [a, b] = [Math.min(x0, x1), Math.max(x0, x1)];
  const [c, d] = [Math.min(y0, y1), Math.max(y0, y1)];
  return [[a, c], [b, c], [b, d], [a, d]];
};
/** Pins drawn closer than this many pixels merge into one group marker. */
const GROUP_PX = 28;
const TONE_RANK = { critical: 3, warning: 2, idle: 1, success: 0 };
const plural = (name, n) => (n > 1 && !/s$/i.test(name) ? `${name}s` : name);
const catName = (d) => d.category_name || 'Device';

/** Single-link clusters of pins whose drawn positions are within GROUP_PX. Returns arrays of items { d, x, y } (pixels). */
function clusterPins(items) {
  const parent = items.map((_, i) => i);
  const find = (i) => { while (parent[i] !== i) { parent[i] = parent[parent[i]]; i = parent[i]; } return i; };
  for (let i = 0; i < items.length; i += 1) {
    for (let j = i + 1; j < items.length; j += 1) {
      if (Math.hypot(items[i].x - items[j].x, items[i].y - items[j].y) < GROUP_PX) parent[find(i)] = find(j);
    }
  }
  const by = new Map();
  items.forEach((it, i) => { const r = find(i); if (!by.has(r)) by.set(r, []); by.get(r).push(it); });
  return [...by.values()];
}

/** "3 Laptops · 2 Monitors · 1 IoT", largest first. */
function categorySummary(devices) {
  const n = new Map();
  devices.forEach((d) => n.set(catName(d), (n.get(catName(d)) || 0) + 1));
  return [...n.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0])).map(([k, c]) => `${c} ${plural(k, c)}`).join(' · ');
}

const round = (v) => Math.round(v * 1000) / 1000;

/**
 * A room's name inside its outline: wrapped onto as many lines as it needs
 * and shrunk until it fits the box (12px at most, so it does not crowd the
 * pins), so a cupboard gets small print and a boardroom larger. Refitted when the box changes size (zoom, full width).
 */
function FittedName({ name }) {
  const ref = useRef(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    // Shrink whole words first; break a word only when the smallest print
    // still does not fit (a long name in a cupboard).
    const fit = () => {
      const over = () => el.scrollHeight > el.clientHeight + 1 || el.scrollWidth > el.clientWidth + 1;
      let size = 12;
      el.style.overflowWrap = 'normal';
      el.style.fontSize = `${size}px`;
      while (size > 7 && over()) {
        size -= 1;
        el.style.fontSize = `${size}px`;
      }
      if (over()) el.style.overflowWrap = 'anywhere';
    };
    fit();
    const watch = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(fit);
    if (watch) watch.observe(el);
    return () => { if (watch) watch.disconnect(); };
  }, [name]);
  // The text is its own span: only it, not the whole box, takes the pointer.
  return <span ref={ref} className="as-map-area-name"><span className="as-map-area-text">{name}</span></span>;
}

export default function AssetMap() {
  const { locationId } = useParams();
  const navigate = useNavigate();
  const meta = useAssetMeta();
  const [confirm, confirmDialog] = useConfirm();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [placing, setPlacing] = useState(null);   // the device id waiting for a click on the plan
  // "Label a room": { room, text, shape: 'name' | 'rect' | 'corners', points: [[x, y]...], drag: [x, y] | null }
  const [labelling, setLabelling] = useState(null);
  const [dragging, setDragging] = useState(null); // { kind: 'pin' | 'label', id, from, orig }
  const [hover, setHover] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [planSrc, setPlanSrc] = useState(null);
  const [natural, setNatural] = useState(null);   // the picture's own size, for a crop's proportions
  const [wide, setWide] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [showRetired, setShowRetired] = useState(false);
  // 103: where my Map opens - my chosen building, or the only one - and
  // the buildings I may choose from. Fetched once.
  const [defaults, setDefaults] = useState(null);
  const arrived = useRef(false);
  const [editing, setEditing] = useState(false);   // "Edit outlines": handles on every outline
  const [overArea, setOverArea] = useState(null);  // the outline under the pointer: its label's tools show
  const [building, setBuilding] = useState(null);  // the floors of this place's building(s), or null
  const [tab, setTab] = useState('plan');
  const [storeOpen, setStoreOpen] = useState(null);  // the storage place whose panel is open (104)
  const [tidy, setTidy] = useState(null);          // { busy } while asking, then { proposals, keep: Set, dropped }
  // 108: one pin per desk. The desk waiting for a click on the plan, and
  // "Add a desk": { name, parent_id } until the click that drops its pin.
  const [placingDesk, setPlacingDesk] = useState(null);
  // 111: "Size" on a desk - drag a rectangle on the plan for how much room it takes.
  const [sizingDesk, setSizingDesk] = useState(null);
  // The map is LOCKED until "Unlock editing" (Gordon, 29 October: clicking a
  // room kept dragging its outline). Locked, nothing on the plan can be
  // dragged, labelled, placed or sized; every visit starts locked.
  const [unlocked, setUnlocked] = useState(false);
  const [deskDraw, setDeskDraw] = useState(null);   // { a, b } in fractions of the picture, while dragging
  const [addingDesk, setAddingDesk] = useState(null);
  const planRef = useRef(null);
  const [canvasPx, setCanvasPx] = useState({ w: 0, h: 0 });
  const [availH, setAvailH] = useState(0);   // the window's height below the top of the plan: the plan is fitted to it
  const [openGroup, setOpenGroup] = useState(null);
  // The list is split into devices with no pin and devices with one (108),
  // each cut by room (109). EVERYTHING IS FOLDED on every visit and on every
  // move to another place, and nothing is remembered (Gordon, 29 October):
  // both halves and every room open only when clicked, or when a pin is.
  const [unpinnedOpen, setUnpinnedOpen] = useState(false);
  const [pinnedOpen, setPinnedOpen] = useState(false);
  const togglePinned = () => setPinnedOpen((v) => !v);
  const [roomsOpen, setRoomsOpen] = useState({});
  const toggleRoom = (key) => setRoomsOpen((m) => {
    const next = { ...m };
    if (next[key]) delete next[key]; else next[key] = true;
    return next;
  });
  useEffect(() => { setUnpinnedOpen(false); setPinnedOpen(false); setRoomsOpen({}); }, [locationId]);
  const [selected, setSelected] = useState(null);   // the device whose pin was last clicked
  const [planAway, setPlanAway] = useState(false);  // the plan has scrolled out of sight (phones)
  const colRef = useRef(null);
  useEffect(() => {
    if (!selected) return;
    const row = document.querySelector(`.as-map-list li[data-device="${selected}"]`);
    if (row && row.scrollIntoView) row.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [selected, roomsOpen, pinnedOpen, unpinnedOpen]);
  const colPlace = data?.place?.id;
  useEffect(() => {
    const el = colRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver(([e]) => setPlanAway(!e.isIntersecting), { threshold: 0 });
    io.observe(el);
    return () => io.disconnect();
  }, [colPlace, tab]);
  const fileRef = useRef(null);
  // Whether the pointer moved since it went down on a pin or a label: a
  // press without a move is a click (open the device or the room), not a drag.
  const moved = useRef(false);

  const load = useCallback(() => {
    if (!locationId) { setData(null); return; }
    axios.get(`${API_BASE}/assets/map/${locationId}${showRetired ? '?retired=1' : ''}`)
      .then((r) => { setData(r.data); setError(''); })
      .catch((e) => setError(e.response?.status === 404 ? 'There is no such place, or it is not one you may see.'
        : e.response?.data?.error || 'Could not load the map.'));
  }, [locationId, showRetired]);
  useEffect(() => { load(); }, [load]);

  // The Map opened with no place: go to my default, once - choosing
  // "Choose a place…" later stays on the chooser.
  const loadDefaults = useCallback(() => axios.get(`${API_BASE}/assets/map-default`)
    .then((r) => { setDefaults(r.data); return r.data; })
    .catch(() => { setDefaults({ location_id: null, buildings: [], ready: false }); return null; }), []);
  useEffect(() => {
    let live = true;
    loadDefaults().then((d) => {
      if (!live || arrived.current) return;
      arrived.current = true;
      if (!locationId && d?.location_id) navigate(`/assets/map/${d.location_id}`, { replace: true });
    });
    return () => { live = false; };
    // Once, on arrival: later changes of place must not bounce back.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const makeDefault = async (id) => {
    setError('');
    try {
      await axios.put(`${API_BASE}/assets/map-default`, { location_id: id });
      await loadDefaults();
    } catch (e) {
      setError(e.response?.data?.error || 'Could not save your default building.');
    }
  };
  // The building's floors, for the 3D view. A failed or missing answer is "no floors".
  useEffect(() => {
    setBuilding(null);
    setTab('plan');
    if (!locationId) return undefined;
    let live = true;
    axios.get(`${API_BASE}/assets/map/${locationId}/building`)
      .then((r) => {
        const list = (r.data?.buildings || []).filter((b) => Array.isArray(b.floors) && b.floors.length > 0);
        if (live && list.length) setBuilding(list);
      })
      .catch(() => {});
    return () => { live = false; };
  }, [locationId]);
  useEffect(() => { setPlacing(null); setLabelling(null); setZoom(1); setEditing(false); setTidy(null); setPlacingDesk(null); setAddingDesk(null); setSizingDesk(null); setDeskDraw(null); setUnlocked(false); }, [locationId]);
  useEffect(() => {
    const onKey = (ev) => { if (ev.key === 'Escape') { setPlacing(null); setLabelling(null); setPlacingDesk(null); setSizingDesk(null); setDeskDraw(null); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // The canvas's drawn size, for merging pins that sit too close together to tell apart.
  const planShown = Boolean(data) && Boolean(data.place?.has_plan || data.crop);
  useEffect(() => {
    const el = planRef.current;
    if (!el) { setCanvasPx({ w: 0, h: 0 }); return undefined; }
    const measure = () => {
      const frame = el.parentElement;
      if (frame) {
        const top = frame.getBoundingClientRect().top + window.scrollY;
        const room = Math.max(240, Math.round(window.innerHeight - top - 12));
        setAvailH((h) => (Math.abs(h - room) < 2 ? h : room));
      }
      const r = el.getBoundingClientRect();
      setCanvasPx((c) => (Math.round(c.w) === Math.round(r.width) && Math.round(c.h) === Math.round(r.height) ? c : { w: r.width, h: r.height }));
    };
    measure();
    // The page is watched too: the header and intro above the plan settle
    // after the first draw, and the plan's fitted height must follow them.
    const watch = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    if (watch) { watch.observe(el); watch.observe(document.body); }
    window.addEventListener('resize', measure);
    return () => { if (watch) watch.disconnect(); window.removeEventListener('resize', measure); };
  }, [planShown, zoom, wide, tab, locationId]);

  const crop = data?.crop || null;
  const shown = Boolean(data?.place?.has_plan || crop);
  const hasFloors = Boolean(building && building.length);
  const showBuilding = hasFloors && (!shown || tab === 'building');

  // The picture is behind the same sign-in as the rest of the API, and a
  // plain <img> carries no token. So it is fetched like any other request
  // (the interceptors add the bearer) and shown from a blob. The URL
  // carries the plan's version, so a replaced plan is a new fetch. A crop
  // shows the picture of the plan it is cut from.
  const planUrl = data?.place?.plan_url || crop?.plan_url || null;
  useEffect(() => {
    if (!planUrl) { setPlanSrc(null); return undefined; }
    let live = true;
    let made = null;
    axios.get(`${API_BASE}${planUrl}`, { responseType: 'blob' })
      .then((r) => {
        if (!live) return;
        made = URL.createObjectURL(r.data);
        setPlanSrc(made);
      })
      .catch((e) => { if (live) setError(e.response?.data?.error || 'Could not load the floor plan picture.'); });
    return () => { live = false; if (made) URL.revokeObjectURL(made); };
  }, [planUrl]);

  // The part of the picture shown: all of it, or the crop's outline box.
  const view = crop ? boxOf(crop.area) : WHOLE;
  // The plan is first drawn whole inside the window: as wide as the column
  // allows, but no taller than the room below its top. Zooming scales from there.
  const ratio = natural?.w && natural?.h ? (crop ? (view.w * natural.w) / (view.h * natural.h) : natural.w / natural.h) : 0;
  const fitW = (z) => (ratio > 0 && availH > 0 ? `min(${z * 100}%, ${Math.round(z * availH * ratio)}px)` : `${z * 100}%`);
  const toView = (x, y) => ({ x: (Number(x) - view.x) / view.w, y: (Number(y) - view.y) / view.h });
  const fromView = (p) => ({ x: round(view.x + p.x * view.w), y: round(view.y + p.y * view.h) });

  /** Where the pointer is, as fractions of the whole picture. */
  const fraction = (ev) => {
    const box = planRef.current?.getBoundingClientRect();
    if (!box || !box.width || !box.height) return null;
    const x = clamp01((ev.clientX - box.left) / box.width);
    const y = clamp01((ev.clientY - box.top) / box.height);
    return fromView({ x, y });
  };

  const pin = async (id, at) => {
    setError('');
    try {
      await axios.post(`${API_BASE}/assets/${id}/pin`,
        at ? { x: at.x, y: at.y, on: crop ? crop.location_id : locationId } : { x: null });
      load();
    } catch (e) {
      setError(e.response?.data?.error || 'Could not place the pin.');
      load();
    }
  };

  /** A desk's one pin (108), on the plan the page shows; null takes it off. */
  const pinDesk = async (id, at) => {
    setError('');
    try {
      await axios.post(`${API_BASE}/assets/desks/${id}/pin`,
        at ? { x: at.x, y: at.y, on: crop ? crop.location_id : locationId } : { x: null });
    } catch (e) {
      setError(e.response?.data?.error || 'Could not place the desk.');
    }
    load();
  };

  /** A desk's size (111): w and h as fractions of the picture, about its centre; null makes it a pin again. */
  const sizeDesk = async (id, box) => {
    setError('');
    try {
      await axios.post(`${API_BASE}/assets/desks/${id}/size`, box || { w: null });
    } catch (e) {
      setError(e.response?.data?.error || 'Could not size the desk.');
    }
    load();
  };

  /** "Add a desk": made under the room chosen, with its pin where the plan was clicked (or none). */
  const addDesk = async (at) => {
    const d = addingDesk;
    setAddingDesk(null);
    setError('');
    try {
      await axios.post(`${API_BASE}/assets/desks`, { name: d.name.trim(), parent_id: d.parent_id || locationId, ...(at ? { x: at.x, y: at.y } : {}) });
    } catch (e) {
      setError(e.response?.data?.error || 'Could not add the desk.');
    }
    load();
  };
  const deskReady = Boolean(addingDesk && addingDesk.name.trim());

  const saveLabel = async (at, area) => {
    const l = labelling;
    setLabelling(null);
    setError('');
    const existing = l.room && (data.labels || []).find((x) => x.label_location_id === l.room);
    try {
      if (existing && area) {
        await axios.patch(`${API_BASE}/assets/map/${locationId}/labels/${existing.id}`, { area });
      } else {
        const body = l.room ? { label_location_id: l.room } : { text: l.text.trim() };
        await axios.post(`${API_BASE}/assets/map/${locationId}/labels`, area ? { ...body, area } : { ...body, x: at.x, y: at.y });
      }
      load();
    } catch (e) {
      setError(e.response?.data?.error || 'Could not save the label.');
    }
  };

  const changeLabel = async (id, body, message) => {
    setError('');
    try {
      await axios.patch(`${API_BASE}/assets/map/${locationId}/labels/${id}`, body);
      load();
    } catch (e) {
      setError(e.response?.data?.error || message);
      load();
    }
  };

  const removeLabel = async (id) => {
    setError('');
    try {
      await axios.delete(`${API_BASE}/assets/map/${locationId}/labels/${id}`);
      load();
    } catch (e) {
      setError(e.response?.data?.error || 'Could not remove the label.');
    }
  };

  const named = labelling && (labelling.room || labelling.text.trim());
  const outlining = Boolean(named && labelling.shape !== 'name');

  /** Closing a corner outline: three corners or more, then a click on the first, a double-click, or Finish. */
  const finishCorners = () => {
    if (labelling?.points?.length >= 3) saveLabel(null, labelling.points.map((p) => [p.x, p.y]));
  };

  const onPlanClick = (ev) => {
    if (placingDesk || deskReady) {
      const at = fraction(ev);
      if (!at) return;
      if (placingDesk) { const id = placingDesk; setPlacingDesk(null); pinDesk(id, at); } else addDesk(at);
      return;
    }
    if (placing) {
      const at = fraction(ev);
      if (!at) return;
      const id = placing;
      setPlacing(null);
      pin(id, at);
      return;
    }
    if (!named) return;
    const at = fraction(ev);
    if (!at) return;
    if (labelling.shape === 'name') { saveLabel(at, null); return; }
    if (labelling.shape === 'corners') {
      const pts = labelling.points;
      const first = pts[0];
      if (pts.length >= 3 && Math.abs(first.x - at.x) < 0.015 && Math.abs(first.y - at.y) < 0.015) { finishCorners(); return; }
      const last = pts[pts.length - 1];
      if (last && Math.abs(last.x - at.x) < 0.005 && Math.abs(last.y - at.y) < 0.005) return; // the second click of a double-click
      setLabelling({ ...labelling, points: [...pts, at] });
    }
  };

  const onDown = (ev) => {
    if (sizingDesk && ev.button === 0) {
      const at = fraction(ev);
      if (!at) return;
      ev.preventDefault();
      setDeskDraw({ a: at, b: at });
      return;
    }
    if (!outlining || labelling.shape !== 'rect' || ev.button !== 0) return;
    const at = fraction(ev);
    if (!at) return;
    ev.preventDefault();
    setLabelling({ ...labelling, points: [at], drag: at });
  };

  // Dragging a pin or a label: it follows the pointer in local state and is
  // saved on release, so one drag is one request. An outlined room moves as
  // a whole, kept on the picture.
  const onMove = (ev) => {
    if (deskDraw) {
      const at = fraction(ev);
      if (at) setDeskDraw((d) => ({ ...d, b: at }));
      return;
    }
    if (outlining && labelling.shape === 'rect' && labelling.drag) {
      const at = fraction(ev);
      if (at) setLabelling({ ...labelling, drag: at });
      return;
    }
    if (!dragging) return;
    // A press that wobbles a pixel or two is still a click (opening a room,
    // selecting a pin), not a drag that nudges it.
    if (!moved.current && Math.hypot(ev.clientX - dragging.cx, ev.clientY - dragging.cy) < DRAG_PX) return;
    const at = fraction(ev);
    if (!at) return;
    moved.current = true;
    if (dragging.kind === 'pin') {
      setData((d) => ({ ...d, devices: d.devices.map((x) => (x.id === dragging.id ? { ...x, map_x: at.x, map_y: at.y } : x)) }));
      return;
    }
    if (dragging.kind === 'corner' || dragging.kind === 'side') {
      const area = dragging.orig.area.map((p) => [Number(p[0]), Number(p[1])]);
      let next;
      if (dragging.kind === 'side') {
        const [[x0, y0], , [x1, y1]] = area;
        const s = dragging.side;
        next = rectFrom(s === 'l' ? at.x : x0, s === 't' ? at.y : y0, s === 'r' ? at.x : x1, s === 'b' ? at.y : y1);
      } else if (isRect(area)) {
        const far = area[(dragging.index + 2) % 4];
        next = rectFrom(at.x, at.y, far[0], far[1]);
      } else {
        next = area.map((p, i) => (i === dragging.index ? [at.x, at.y] : p));
      }
      const b = boxOf(next);
      if (b.w < 0.01 || b.h < 0.01) return;
      setData((d) => ({ ...d, labels: d.labels.map((x) => (x.id === dragging.id ? { ...x, area: next.map((p) => [round(p[0]), round(p[1])]) } : x)) }));
      return;
    }
    const { orig, from } = dragging;
    let dx = at.x - from.x;
    let dy = at.y - from.y;
    if (orig.area) {
      const b = boxOf(orig.area);
      dx = Math.min(1 - (b.x + b.w), Math.max(-b.x, dx));
      dy = Math.min(1 - (b.y + b.h), Math.max(-b.y, dy));
    }
    setData((d) => ({ ...d,
      labels: d.labels.map((x) => (x.id !== dragging.id ? x : {
        ...x,
        x: round(clamp01(Number(orig.x) + dx)),
        y: round(clamp01(Number(orig.y) + dy)),
        area: orig.area ? orig.area.map((p) => [round(Number(p[0]) + dx), round(Number(p[1]) + dy)]) : x.area
      })) }));
  };

  const onUp = (ev) => {
    if (deskDraw) {
      const { a } = deskDraw;
      const b = fraction(ev) || deskDraw.b;
      const id = sizingDesk;
      setDeskDraw(null);
      const w = Math.abs(a.x - b.x);
      const h = Math.abs(a.y - b.y);
      if (w < 0.005 || h < 0.005) return; // a click, not a rectangle: keep waiting for one
      setSizingDesk(null);
      sizeDesk(id, { x: round((a.x + b.x) / 2), y: round((a.y + b.y) / 2), w: round(w), h: round(h) });
      return;
    }
    if (outlining && labelling.shape === 'rect' && labelling.drag) {
      const a = labelling.points[0];
      const b = fraction(ev) || labelling.drag;
      if (Math.abs(a.x - b.x) < 0.01 || Math.abs(a.y - b.y) < 0.01) { setLabelling({ ...labelling, points: [], drag: null }); return; }
      saveLabel(null, [[a.x, a.y], [b.x, a.y], [b.x, b.y], [a.x, b.y]]);
      return;
    }
    if (!dragging) return;
    const { kind, id } = dragging;
    setDragging(null);
    if (!moved.current) return;
    if (kind === 'pin') {
      const at = fraction(ev);
      if (at) pin(id, at);
      return;
    }
    const l = data.labels.find((x) => x.id === id);
    if (kind === 'corner' || kind === 'side') {
      if (l) changeLabel(id, { area: l.area }, 'Could not resize the outline.');
      return;
    }
    if (!l) return;
    changeLabel(id, l.area ? { x: Number(l.x), y: Number(l.y), area: l.area } : { x: Number(l.x), y: Number(l.y) },
      'Could not move the label.');
  };

  const startDrag = (kind, id, extra = {}) => (ev) => {
    if (!mapEdit || ev.button !== 0 || placing || labelling || tidy) return;
    ev.preventDefault();
    ev.stopPropagation();
    moved.current = false;
    const at = fraction(ev);
    const orig = kind === 'pin' ? null : data.labels.find((x) => x.id === id);
    setDragging({ kind, id, from: at, orig, cx: ev.clientX, cy: ev.clientY, ...extra });
  };

  /** "Tidy with AI": ask for proposals; nothing is saved until Apply. */
  const askTidy = async () => {
    setError('');
    setPlacing(null);
    setLabelling(null);
    setTidy({ busy: true });
    try {
      const r = await axios.post(`${API_BASE}/assets/map/${locationId}/labels/refine`, {});
      const proposals = r.data.proposals || [];
      setTidy({ proposals, dropped: r.data.dropped || 0,
        keep: new Set(proposals.filter((p) => p.confidence === 'high').map((p) => p.id)) });
    } catch (e) {
      setTidy(null);
      setError(e.response?.data?.error || 'Could not tidy the outlines.');
    }
  };
  const applyTidy = async () => {
    const chosen = tidy.proposals.filter((p) => tidy.keep.has(p.id));
    setTidy({ ...tidy, busy: true });
    setError('');
    try {
      for (const p of chosen) {
        // One at a time: each is the ordinary PATCH, under the person's own write.
        // eslint-disable-next-line no-await-in-loop
        await axios.patch(`${API_BASE}/assets/map/${locationId}/labels/${p.id}`, { area: p.area });
      }
      setTidy(null);
    } catch (e) {
      setTidy({ ...tidy, busy: false });
      setError(e.response?.data?.error || 'Could not save the tidied outlines.');
    }
    load();
  };
  const toggleKeep = (id) => setTidy((t) => {
    const keep = new Set(t.keep);
    if (keep.has(id)) keep.delete(id); else keep.add(id);
    return { ...t, keep };
  });

  /** A click on a room - its outline or its name (not a drag) - opens the room's own map. */
  const openRoom = (l) => (ev) => {
    if (placing || labelling) return; // the click is the plan's: a pin or a corner
    if (editing || tidy) { ev.stopPropagation(); moved.current = false; return; }
    ev.stopPropagation();
    const was = moved.current;
    moved.current = false;
    // A storage area's icon opens its list, not a room crop (104).
    if (l.storage && l.label_location_id) { if (!was) setStoreOpen(l.label_location_id); return; }
    if (was || !l.label_location_id) return;
    navigate(`/assets/map/${l.label_location_id}`);
  };

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    setError('');
    try {
      const form = new FormData();
      form.append('file', file);
      await axios.put(`${API_BASE}/assets/map/${locationId}/plan`, form);
      load();
    } catch (e) {
      setError(e.response?.data?.error || 'Could not upload the plan.');
    } finally { setUploading(false); }
  };

  const removePlan = async () => {
    if (!(await confirm({ title: 'Remove the floor plan?', message: 'Every pin, room label and room outline on it goes too.', confirmLabel: 'Remove', danger: true }))) return;
    try {
      await axios.delete(`${API_BASE}/assets/map/${locationId}/plan`);
      load();
    } catch (e) {
      setError(e.response?.data?.error || 'Could not remove the plan.');
    }
  };

  const places = meta?.locations || [];
  const placeId = data?.place?.id;
  const planId = crop ? crop.location_id : placeId;
  const onThisPlan = (d) => d.plan_location_id === planId;
  const inView = (p) => p.x >= -0.001 && p.x <= 1.001 && p.y >= -0.001 && p.y <= 1.001;
  const pinned = (data?.devices || []).filter((d) => onThisPlan(d) && d.map_x !== null && inView(toView(d.map_x, d.map_y)));
  const clusters = canvasPx.w > 0 && canvasPx.h > 0
    ? clusterPins(pinned.filter((d) => !(dragging?.kind === 'pin' && dragging.id === d.id))
      .map((d) => { const a = toView(d.map_x, d.map_y); return { d, x: a.x * canvasPx.w, y: a.y * canvasPx.h, vx: a.x, vy: a.y }; }))
    : [];
  const dragged = dragging?.kind === 'pin' ? pinned.filter((d) => d.id === dragging.id) : [];
  // Kept in the order of `pinned`, so a pin pressed for a drag is not moved in the page (a moved button loses its click).
  const singles = canvasPx.w > 0
    ? [...clusters.filter((c) => c.length === 1).map((c) => c[0].d), ...dragged].sort((x, y) => pinned.indexOf(x) - pinned.indexOf(y))
    : pinned;
  const bunches = clusters.filter((c) => c.length > 1);
  const unpinnedList = data ? data.devices.filter((d) => d.map_x === null || d.map_x === undefined) : [];
  const pinnedList = data ? data.devices.filter((d) => d.map_x !== null && d.map_x !== undefined) : [];
  const roomCount = data ? byRoom(data.devices, placeId).length : 0;
  // 108: a desk's kit is listed under its desk, not in the device list, but
  // the place's label print (a ?location= filter) prints it too.
  const deskKit = (data?.desks || []).reduce((n, k) => n + (k.kit || []).length, 0);
  // Labels are printed a room or a desk at a time, not a whole floor (Gordon,
  // 29 October): a place with rooms beneath it - a floor, a building - offers none.
  const isFloor = !data?.crop && (data?.children || []).some((c) => (c.kind || 'area') === 'area');
  const allRoomKeys = data ? byRoom(data.devices, placeId).filter((g) => roomCount > 1 || !g.here).map((g) => g.key) : [];
  const openAllRooms = () => { setUnpinnedOpen(true); setPinnedOpen(true); setRoomsOpen(Object.fromEntries(allRoomKeys.map((k) => [k, true]))); };
  const foldAllRooms = () => { setUnpinnedOpen(false); setPinnedOpen(false); setRoomsOpen({}); };
  // A pin was clicked: open its half and its room, then bring its row into view.
  const selectDevice = (d) => {
    setSelected(d.id);
    if (d.map_x === null || d.map_x === undefined) setUnpinnedOpen(true); else setPinnedOpen(true);
    setRoomsOpen((m) => (m[d.location_id || placeId] ? m : { ...m, [d.location_id || placeId]: true }));
  };
  const renderGroups = (list) => (
    <>
      {byRoom(list, placeId).map((g) => {
        const headed = roomCount > 1 || !g.here;
        const isOpen = !headed || Boolean(roomsOpen[g.key]);
        const title = g.here ? `At ${data.place.name}` : g.name;
        return (
        <section key={g.key} className="as-map-group" aria-label={g.here ? data.place.name : g.name}>
          {headed && (
            <h3>
              <button type="button" className="as-map-roombtn" aria-expanded={isOpen} data-testid="room-head" data-room={g.key}
                onClick={() => toggleRoom(g.key)}>
                <span aria-hidden="true">{isOpen ? '▾ ' : '▸ '}</span>{`${title} (${g.devices.length})`}
              </button>
            </h3>
          )}
          {isOpen && (
          <ul className="as-tree">
            {g.devices.map((d) => {
              const here = onThisPlan(d);
              const placed = here && d.map_x !== null;
              return (
                <li key={d.id} className="as-map-row" data-hover={String(hover === d.id)} data-selected={String(selected === d.id)} data-device={d.id}
                  onMouseEnter={() => setHover(d.id)} onMouseLeave={() => setHover(null)}
                  onFocus={() => setHover(d.id)} onBlur={() => setHover(null)}>
                  <span className="as-pin-badge" data-tone={pinStatus(d).tone} data-fixed={String(Boolean(d.fixed))}
                    title={`${pinStatus(d).label} · ${d.fixed ? 'fixed' : 'moves around'}`}>
                    <CategoryIcon category={d.category_name} />
                  </span>
                  <span className="as-map-row-main">
                    <span className="as-map-row-top">
                      <strong>{[d.category_name, d.manufacturer, d.model].filter(Boolean).join(' ')}</strong>
                      <Link className="as-map-tag" to={`/assets/${d.id}`}>{tagText(d)}</Link>
                    </span>
                    <span className="as-quiet">
                      {[d.holder_name ? holderLabel(d) : d.is_shared ? 'shared' : '', d.ip_address,
                        d.last_seen_at || d.last_heartbeat_at ? `seen ${ago(d.last_seen_at || d.last_heartbeat_at)}` : ''].filter(Boolean).join(' · ')}
                    </span>
                    {(d.lifecycle_status !== 'deployed' || (!here && d.plan_location_id)) && (
                      <span className="as-map-row-chips">
                        {d.lifecycle_status !== 'deployed' && <span className="as-chip" data-tone={lifecycleTone(d.lifecycle_status)}>{LIFECYCLE_LABEL[d.lifecycle_status]}</span>}
                        {!here && d.plan_location_id && <Link className="as-chip" data-tone="info" to={`/assets/map/${d.plan_location_id}`}>{`On ${d.plan_location_name}’s plan`}</Link>}
                      </span>
                    )}
                  </span>
                  {mapEdit && shown && here && (
                    <span className="as-map-row-acts">
                      <button type="button" className="btn btn-plain btn-small" disabled={placing === d.id}
                        aria-label={`${placed ? 'Move' : 'Place'} ${tagText(d)}`}
                        onClick={() => { setLabelling(null); setPlacing(d.id); }}>{placed ? 'Move' : 'Place'}</button>
                      {placed && (
                        <button type="button" className="btn btn-plain btn-small" aria-label={`Unpin ${tagText(d)}`} onClick={() => pin(d.id, null)}>Unpin</button>
                      )}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
          )}
        </section>
        );
      })}
    </>
  );
  const labels = data?.labels || [];
  // OUTLINES IN LAYERS: largest first, so a room inside an open-plan area or
  // a corridor is drawn - and caught - above it, and every room is reachable.
  // The outline being moved or resized comes to the top while it is; names
  // without an outline last. The same order is the Tab order: the area
  // first, then the rooms within it.
  const lift = dragging && dragging.kind !== 'pin' ? dragging.id : null;
  // Rooms of the same size go in reading order: top to bottom, left to right.
  const rank = (l) => (!l.area ? -1 : l.id === lift ? 0 : Math.round(areaOf(l.area) * 1e6));
  const stack = labels.slice().sort((a, b) => rank(b) - rank(a)
    || (a.area && b.area ? boxOf(a.area).y - boxOf(b.area).y || boxOf(a.area).x - boxOf(b.area).x : 0));
  const labelled = new Set(labels.map((l) => l.label_location_id).filter(Boolean));
  // A name alone goes on a room not yet named; an outline may be added to one that is.
  const choosable = (data?.rooms || []).filter((r) => labelling?.shape !== 'name' || !labelled.has(r.id));
  const mapEdit = Boolean(data?.write && unlocked);
  const canLabel = Boolean(mapEdit && data?.place?.has_plan && data?.labels_ready);
  /** Locking again puts away whatever was half done. */
  const lockMap = () => {
    setUnlocked(false); setEditing(false); setPlacing(null); setLabelling(null); setPlacingDesk(null);
    setAddingDesk(null); setSizingDesk(null); setDeskDraw(null); setTidy(null);
  };
  const hoverRoom = (data?.devices || []).find((d) => d.id === hover)?.location_id || null;
  const pct = (v) => `${v * 100}%`;

  // The outline being drawn, in the view's coordinates.
  let drawn = [];
  if (labelling?.shape === 'rect' && labelling.drag && labelling.points[0]) {
    const a = labelling.points[0];
    const b = labelling.drag;
    drawn = [a, { x: b.x, y: a.y }, b, { x: a.x, y: b.y }];
  } else if (labelling?.shape === 'corners') drawn = labelling.points;

  const startLabelling = () => { setPlacing(null); setLabelling({ room: '', text: '', shape: data.areas_ready ? 'rect' : 'name', points: [], drag: null }); };
  const prompt = () => {
    if (!named) return '';
    if (labelling.shape === 'name') return 'then click on the plan where it is.';
    if (labelling.shape === 'rect') return 'then drag a rectangle over the room on the plan.';
    return labelling.points.length >= 3 ? 'click the first corner (or Finish) to close it.' : 'then click each corner of the room in turn.';
  };

  return (
    <div className="container">
      <p><Link to="/assets">← Assets</Link></p>
      {data?.place?.parent && locationId === data.place.id && (
        <p className="as-bv-back"><Link to={`/assets/map/${data.place.parent.id}`}>{`← ${data.place.parent.name}`}</Link></p>
      )}
      {/* The place and the title on the left, what the page is for beside them
          on the right, so the plan starts higher (Gordon, 29 October). */}
      <div className="as-map-top">
      <div className="as-map-top-main">
      <div className="as-toolbar as-map-placebar">
        <select value={locationId || ''} aria-label="Place" onChange={(e) => navigate(e.target.value ? `/assets/map/${e.target.value}` : '/assets/map')}>
          <option value="">Choose a place…</option>
          {places.map((l) => <option key={l.id} value={l.id}>{l.path}</option>)}
        </select>
        <span className="as-spacer" />
        {locationId && defaults?.ready && defaults.buildings?.length > 1 && defaults.buildings.some((b) => b.id === locationId) && (
          defaults.location_id === locationId && defaults.why === 'chosen'
            ? <span className="as-chip" data-tone="success" data-testid="your-default">Your default</span>
            : <button type="button" className="btn btn-plain btn-small" onClick={() => makeDefault(locationId)}>Make this my default</button>
        )}
      </div>
      <div className="page-header as-map-head">
        <h1 className="page-title">
          Map
          {data?.place?.name && <span className="as-map-floorname" data-testid="floor-name">{data.place.name}</span>}
        </h1>
      </div>
      </div>
      <p className="page-description as-map-intro">
        A floor plan per place, with the devices kept there and in the rooms beneath it pinned where they stand,
        and the rooms named and outlined on it. Choose a place; upload its plan; place each device with a click,
        or drag a pin to move it.
      </p>
      </div>

      <div className="as-toolbar as-map-actions">
        {data?.write && (
          <button type="button" className={`btn ${unlocked ? 'btn-primary' : 'btn-secondary'} as-map-lock`} aria-pressed={unlocked}
            data-testid="map-lock" onClick={() => (unlocked ? lockMap() : setUnlocked(true))}
            title={unlocked ? 'Lock the map so a click on a room cannot move it' : 'Allow labelling rooms, moving outlines and pins, and placing desks'}>
            {unlocked ? 'Lock the map' : 'Unlock editing'}
          </button>
        )}
        {canLabel && (
          <button type="button" className="btn btn-secondary" disabled={Boolean(labelling)} onClick={startLabelling}>Label a room</button>
        )}
        {/* A desk and a plan belong to a floor or a room, not a building (Gordon, 29 October). */}
        {mapEdit && data?.desks_ready && !hasFloors && (
          <button type="button" className="btn btn-secondary" disabled={Boolean(addingDesk)}
            onClick={() => { setPlacing(null); setLabelling(null); setPlacingDesk(null); setAddingDesk({ name: '', parent_id: '' }); }}>
            Add a desk
          </button>
        )}
        {mapEdit && (
          <>
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" style={{ display: 'none' }}
              onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ''; }} />
            {(!hasFloors || data.place.has_plan) && (
              <button type="button" className="btn btn-secondary" disabled={uploading} onClick={() => fileRef.current?.click()}>
                {data.place.has_plan ? 'Replace the plan' : crop ? 'Upload a plan of its own' : 'Upload a plan'}
              </button>
            )}
            {data.place.has_plan && (
              <button type="button" className="btn btn-plain" onClick={removePlan}>Remove the plan</button>
            )}
          </>
        )}
      </div>

      {error && <p className="as-error" role="alert">{error}</p>}
      {meta?.missing && <p className="as-note" data-tone="warning">{meta.error}</p>}

      {!locationId && (defaults?.buildings?.length > 1 && !defaults?.location_id ? (
        <div className="as-panel" data-testid="default-chooser">
          <h2>Which building should the Map open on?</h2>
          <p className="as-quiet">There is more than one. Choose yours; you can change it from any building’s view.</p>
          <ul className="as-tree">
            {defaults.buildings.map((b) => (
              <li key={b.id}>
                <strong>{b.name}</strong>
                <span className="as-quiet">{b.path}</span>
                <span className="as-spacer" />
                <Link to={`/assets/map/${b.id}`}>Open</Link>
                {defaults.ready && (
                  <button type="button" className="btn btn-secondary btn-small"
                    onClick={async () => { await makeDefault(b.id); navigate(`/assets/map/${b.id}`); }}>
                    Make this my default
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <div className="empty-state"><p>Choose a place to see its plan.</p></div>
      ))}

      {data && (
        <>
          {crop && (
            <p className="as-note as-map-cropnote" data-tone="info">
              {`${data.place.name} as outlined on ${crop.location_name}’s plan. `}
              <Link to={`/assets/map/${crop.location_id}`}>{`Open ${crop.location_name}`}</Link>
            </p>
          )}
          {placing && (
            <p className="as-note" data-tone="info">
              Click on the plan where {data.devices.find((d) => d.id === placing)?.asset_tag || 'the device'} stands.
              {' '}<button type="button" className="btn btn-plain btn-small" onClick={() => setPlacing(null)}>Never mind</button>
            </p>
          )}
          {placingDesk && (
            <p className="as-note" data-tone="info">
              {`Click on the plan where ${(data.desks || []).find((k) => k.id === placingDesk)?.name || 'the desk'} stands.`}
              {' '}<button type="button" className="btn btn-plain btn-small" onClick={() => setPlacingDesk(null)}>Never mind</button>
            </p>
          )}
          {sizingDesk && (
            <p className="as-note" data-tone="info" data-testid="size-desk-bar">
              {`Drag a rectangle on the plan over ${(data.desks || []).find((k) => k.id === sizingDesk)?.name || 'the desk'}: its size keeps its share of the room however the plan is zoomed.`}
              {' '}<button type="button" className="btn btn-plain btn-small" onClick={() => { setSizingDesk(null); setDeskDraw(null); }}>Never mind</button>
            </p>
          )}
          {addingDesk && (
            <div className="as-note as-map-labelbar" data-tone="info" data-testid="add-desk-bar">
              <input type="text" aria-label="Desk name" placeholder="Desk 2.14" maxLength={120} value={addingDesk.name}
                onChange={(e) => setAddingDesk({ ...addingDesk, name: e.target.value })} />
              <select aria-label="Room the desk is in" value={addingDesk.parent_id}
                onChange={(e) => setAddingDesk({ ...addingDesk, parent_id: e.target.value })}>
                <option value="">{`In ${data.place.name}`}</option>
                {(data.rooms || []).map((r) => <option key={r.id} value={r.id}>{r.trail}</option>)}
              </select>
              <span>{shown ? 'then click on the plan where it stands, or' : ''}</span>
              <button type="button" className="btn btn-secondary btn-small" disabled={!deskReady} onClick={() => addDesk(null)}>Add without a pin</button>
              <button type="button" className="btn btn-plain btn-small" onClick={() => setAddingDesk(null)}>Never mind</button>
            </div>
          )}
          {labelling && (
            <div className="as-note as-map-labelbar" data-tone="info">
              {data.areas_ready && (
                <select aria-label="What to draw" value={labelling.shape}
                  onChange={(e) => setLabelling({ ...labelling, shape: e.target.value, points: [], drag: null })}>
                  <option value="rect">Outline: drag a rectangle</option>
                  <option value="corners">Outline: click the corners</option>
                  <option value="name">Just the name</option>
                </select>
              )}
              <select aria-label="Room to label" value={labelling.room}
                onChange={(e) => setLabelling({ ...labelling, room: e.target.value, text: '', points: [], drag: null })}>
                <option value="">{choosable.length ? 'Choose a room…' : 'No room left to label'}</option>
                {choosable.map((r) => <option key={r.id} value={r.id}>{r.trail}{labelled.has(r.id) ? ' (named)' : ''}</option>)}
              </select>
              <span className="as-quiet">or</span>
              <input type="text" aria-label="Label text" placeholder="type a name" maxLength={80} value={labelling.text}
                onChange={(e) => setLabelling({ ...labelling, room: '', text: e.target.value, points: [], drag: null })} />
              <span>{prompt()}</span>
              {labelling.shape === 'corners' && labelling.points.length >= 3 && (
                <button type="button" className="btn btn-secondary btn-small" onClick={finishCorners}>Finish</button>
              )}
              <button type="button" className="btn btn-plain btn-small" onClick={() => setLabelling(null)}>Never mind</button>
            </div>
          )}
          {tidy?.proposals && (
            <div className="as-note as-map-tidybar" data-tone="info">
              <p className="as-map-tidyhead">
                <span className="as-chip" data-tone="info">AI</span>
                {tidy.proposals.length
                  ? ` Claude suggests tidier outlines for ${tidy.proposals.length} room${tidy.proposals.length === 1 ? '' : 's'}, drawn dashed. Tick the ones to keep.`
                  : ' Claude had no tidier outlines to suggest.'}
                {tidy.dropped ? ` ${tidy.dropped} answer${tidy.dropped === 1 ? ' was' : 's were'} left out as not making sense.` : ''}
              </p>
              <ul className="as-map-tidylist">
                {tidy.proposals.map((p) => (
                  <li key={p.id}>
                    <label>
                      <input type="checkbox" checked={tidy.keep.has(p.id)} onChange={() => toggleKeep(p.id)} />
                      {' '}<strong>{p.name}</strong>
                      {p.confidence === 'low' ? <span className="as-chip" data-tone="warning">unsure</span> : null}
                      {p.note ? <span className="as-quiet">{` ${p.note}`}</span> : null}
                    </label>
                  </li>
                ))}
              </ul>
              <div className="as-map-tidyactions">
                <button type="button" className="btn btn-primary btn-small" disabled={tidy.busy || !tidy.keep.size} onClick={applyTidy}>
                  {`Apply ${tidy.keep.size || ''}`.trim()}
                </button>
                <button type="button" className="btn btn-plain btn-small" disabled={tidy.busy} onClick={() => setTidy(null)}>Discard</button>
              </div>
            </div>
          )}
          {hasFloors && shown && (
            <div className="as-bv-tabs" role="tablist" aria-label="View">
              <button type="button" role="tab" className="btn btn-plain btn-small" aria-selected={tab === 'building'} onClick={() => setTab('building')}>Building</button>
              <button type="button" role="tab" className="btn btn-plain btn-small" aria-selected={tab === 'plan'} onClick={() => setTab('plan')}>Plan</button>
            </div>
          )}
          {showBuilding && <BuildingView buildings={building} />}
          {!showBuilding && (
          <div className={`as-map${wide ? ' as-map-wide' : ''}`}>
            <div className="as-map-col" ref={colRef}>
              {shown && (
                <div className="as-map-viewbar">
                  <button type="button" className="btn btn-plain btn-small" aria-pressed={wide} onClick={() => setWide((w) => !w)}>
                    {wide ? 'Beside the list' : 'Full width'}
                  </button>
                  {canLabel && labels.some((l) => l.area) && (
                    <button type="button" className="btn btn-plain btn-small" aria-pressed={editing}
                      disabled={Boolean(tidy)} onClick={() => { setPlacing(null); setLabelling(null); setEditing((e) => !e); }}>
                      {editing ? 'Done editing' : 'Edit outlines'}
                    </button>
                  )}
                  {mapEdit && data.ai_tidy && labels.some((l) => l.area) && (
                    <button type="button" className="btn btn-plain btn-small" disabled={Boolean(tidy)} onClick={askTidy}>
                      {tidy?.busy && !tidy.proposals ? 'Tidying…' : 'Tidy with AI'}
                    </button>
                  )}
                  <span className="as-spacer" />
                  <button type="button" className="btn btn-plain btn-small" aria-label="Zoom out" disabled={zoom === ZOOMS[0]}
                    onClick={() => setZoom((z) => ZOOMS[Math.max(0, ZOOMS.indexOf(z) - 1)])}>−</button>
                  <button type="button" className="btn btn-plain btn-small" aria-label="Actual size" onClick={() => setZoom(1)}>{`${Math.round(zoom * 100)}%`}</button>
                  <button type="button" className="btn btn-plain btn-small" aria-label="Zoom in" disabled={zoom === ZOOMS[ZOOMS.length - 1]}
                    onClick={() => setZoom((z) => ZOOMS[Math.min(ZOOMS.length - 1, ZOOMS.indexOf(z) + 1)])}>+</button>
                </div>
              )}
              <div className={`as-map-plan${placing || named || placingDesk || deskReady || sizingDesk ? ' as-map-placing' : ''}${zoom > 1 ? ' as-map-zoomed' : ''}${editing ? ' as-map-editing' : ''}`}>
                {shown ? (
                  // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
                  <div className={`as-map-canvas${crop ? ' as-map-crop' : ''}`} ref={planRef}
                    style={{ width: fitW(zoom),
                      aspectRatio: crop ? `${view.w * (natural?.w || 1)} / ${view.h * (natural?.h || 1)}` : undefined }}
                    onClick={onPlanClick} onMouseDown={onDown} onDoubleClick={() => labelling?.shape === 'corners' && finishCorners()}
                    onMouseMove={onMove} onMouseUp={onUp}
                    onMouseLeave={() => { if (dragging) setDragging(null); }}>
                    {planSrc
                      ? (
                        <img src={planSrc} alt={crop ? `${data.place.name}, cut from the plan of ${crop.location_name}` : `Floor plan of ${data.place.path}`}
                          draggable={false} onLoad={(e) => setNatural({ w: e.target.naturalWidth, h: e.target.naturalHeight })}
                          style={crop ? {
                            width: pct(1 / view.w), left: pct(-view.x / view.w), top: pct(-view.y / view.h),
                            clipPath: `polygon(${crop.area.map((p) => `${p[0] * 100}% ${p[1] * 100}%`).join(', ')})`
                          } : undefined} />
                      )
                      : <p className="as-quiet as-map-loading">Loading the plan…</p>}
                    {/* Desks drawn to size go under the rooms' outlines and names, so a name stays readable. */}
                    {(data.desks || []).filter((k) => k.plan_location_id === planId && k.map_x !== null && k.map_x !== undefined && k.map_w)
                      .map((k) => {
                        // Fractions of the picture, like the pin: the desk keeps its share of the room at any zoom.
                        const w = Number(k.map_w) / view.w;
                        const h = Number(k.map_h) / view.h;
                        const at = toView(k.map_x, k.map_y);
                        return (
                          <span key={`box-${k.id}`} className="as-desk-box" data-testid="desk-box" data-desk={k.id}
                            data-tone={deskTone(k)} data-hover={String(hover === k.id)} aria-hidden="true"
                            style={{ left: pct(at.x - w / 2), top: pct(at.y - h / 2), width: pct(w), height: pct(h) }} />
                        );
                      })}
                    {!crop && (labels.some((l) => l.area) || drawn.length > 0 || tidy?.proposals?.length > 0) && (
                      <svg className="as-map-areas" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
                        {stack.filter((l) => l.area).map((l) => (
                          // The polygon itself is what a click, a hover or a drag
                          // hits: its shape, not the box around it. Keyboard users
                          // reach the room through its name (below).
                          // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
                          <polygon key={l.id} className="as-map-area" data-room={l.label_location_id || ''} data-label={l.id}
                            data-hover={String(Boolean(hoverRoom) && hoverRoom === l.label_location_id)}
                            points={l.area.map((p) => `${p[0] * 100},${p[1] * 100}`).join(' ')}
                            onMouseDown={startDrag('label', l.id)} onClick={openRoom(l)}
                            onMouseEnter={() => setOverArea(l.id)} onMouseLeave={() => setOverArea((o) => (o === l.id ? null : o))} />
                        ))}
                        {drawn.length > 0 && (
                          <polygon className="as-map-drawing" points={drawn.map((p) => `${p.x * 100},${p.y * 100}`).join(' ')} />
                        )}
                        {(tidy?.proposals || []).map((p) => (
                          <polygon key={`tidy-${p.id}`} className="as-map-proposal" data-keep={String(tidy.keep.has(p.id))}
                            points={p.area.map((q) => `${q[0] * 100},${q[1] * 100}`).join(' ')} />
                        ))}
                      </svg>
                    )}
                    {!crop && editing && stack.filter((l) => l.area).map((l) => {
                      const rect = isRect(l.area);
                      const box = boxOf(l.area);
                      const sides = rect ? [['l', box.x, box.y + box.h / 2], ['r', box.x + box.w, box.y + box.h / 2],
                        ['t', box.x + box.w / 2, box.y], ['b', box.x + box.w / 2, box.y + box.h]] : [];
                      return (
                        <React.Fragment key={`h-${l.id}`}>
                          {l.area.map((p, i) => (
                            // eslint-disable-next-line jsx-a11y/no-static-element-interactions
                            <span key={`c${i}`} className="as-map-handle" data-kind="corner" data-room={l.label_location_id || l.id}
                              style={{ left: pct(Number(p[0])), top: pct(Number(p[1])) }}
                              onMouseDown={startDrag('corner', l.id, { index: i })} />
                          ))}
                          {sides.map(([side, x, y]) => (
                            // eslint-disable-next-line jsx-a11y/no-static-element-interactions
                            <span key={side} className="as-map-handle" data-kind="side" data-side={side} data-room={l.label_location_id || l.id}
                              style={{ left: pct(x), top: pct(y) }}
                              onMouseDown={startDrag('side', l.id, { side })} />
                          ))}
                        </React.Fragment>
                      );
                    })}
                    {!crop && labelling?.shape === 'corners' && labelling.points.map((p, i) => (
                      <span key={`${p.x},${p.y}`} className="as-map-corner" data-first={String(i === 0)}
                        style={{ left: pct(p.x), top: pct(p.y) }} />
                    ))}
                    {!crop && stack.map((l) => {
                      const box = l.area && !l.storage ? nameBoxOf(l.area) : null;
                      const name = l.name;
                      if (l.storage) {
                        return (
                          // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
                          <span key={l.id} className="as-map-store" role="button" tabIndex={0}
                            data-writer={String(mapEdit)} data-room={l.label_location_id || ''}
                            data-testid="storage-icon" aria-label={`${name}, ${l.count || 0} devices`} title={name}
                            style={l.area ? { left: pct(boxOf(l.area).x + boxOf(l.area).w / 2), top: pct(boxOf(l.area).y + boxOf(l.area).h / 2) }
                              : { left: pct(Number(l.x)), top: pct(Number(l.y)) }}
                            onMouseDown={startDrag('label', l.id)} onClick={openRoom(l)}
                            onKeyDown={(ev) => { if (ev.key === 'Enter' && l.label_location_id) setStoreOpen(l.label_location_id); }}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                              <rect x="3" y="3" width="18" height="18" rx="2" />
                              <path d="M3 9h18M3 15h18M10 6h4M10 12h4M10 18h4" />
                            </svg>
                            <span className="as-map-store-count">{l.count || 0}</span>
                            {mapEdit && (
                              <span className="as-map-label-tools">
                                <button type="button" className="as-map-label-x" aria-label={`Remove the label ${name}`}
                                  onMouseDown={(ev) => ev.stopPropagation()}
                                  onClick={(ev) => { ev.stopPropagation(); removeLabel(l.id); }}>×</button>
                              </span>
                            )}
                          </span>
                        );
                      }
                      return (
                        // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
                        <span key={l.id} className={box ? 'as-map-label as-map-label-area' : 'as-map-label'}
                          data-writer={String(mapEdit)} data-room={l.label_location_id || ''} data-label={l.id}
                          data-over={String(overArea === l.id)}
                          {...(l.label_location_id ? {
                            role: 'link', tabIndex: 0, 'aria-label': `Open ${name}`,
                            onKeyDown: (ev) => {
                              if (ev.target === ev.currentTarget && ev.key === 'Enter' && !placing && !labelling && !editing && !tidy) {
                                navigate(`/assets/map/${l.label_location_id}`);
                              }
                            }
                          } : {})}
                          style={box
                            ? { left: pct(box.x), top: pct(box.y), width: pct(box.w), height: pct(box.h) }
                            : { left: pct(Number(l.x)), top: pct(Number(l.y)) }}
                          onMouseDown={startDrag('label', l.id)} onClick={openRoom(l)}>
                          {box ? <FittedName name={name} /> : name}
                          {mapEdit && (
                            <span className="as-map-label-tools">
                              {box && (
                                <button type="button" className="as-map-label-x" aria-label={`Remove the outline of ${name}`} title="Keep the name, remove the outline"
                                  onMouseDown={(ev) => ev.stopPropagation()}
                                  onClick={(ev) => { ev.stopPropagation(); changeLabel(l.id, { area: null }, 'Could not remove the outline.'); }}>▭</button>
                              )}
                              <button type="button" className="as-map-label-x" aria-label={`Remove the label ${name}`}
                                onMouseDown={(ev) => ev.stopPropagation()}
                                onClick={(ev) => { ev.stopPropagation(); removeLabel(l.id); }}>×</button>
                            </span>
                          )}
                        </span>
                      );
                    })}
                    {bunches.map((c) => {
                      const key = c.map((i) => i.d.id).sort().join('|');
                      const devs = c.map((i) => i.d);
                      const tone = devs.map((d) => pinStatus(d).tone).sort((a, b) => TONE_RANK[b] - TONE_RANK[a])[0];
                      const cx = c.reduce((t, i) => t + i.vx, 0) / c.length;
                      const cy = c.reduce((t, i) => t + i.vy, 0) / c.length;
                      const isOpen = openGroup === key;
                      const summary = categorySummary(devs);
                      return (
                        // eslint-disable-next-line jsx-a11y/no-static-element-interactions
                        <span key={key} className="as-pin-group" data-testid="pin-group" data-open={String(isOpen)} data-flip={String(cy > 0.55)}
                          style={{ left: pct(cx), top: pct(cy) }}
                          onMouseDown={(ev) => ev.stopPropagation()} onClick={(ev) => ev.stopPropagation()}
                          onMouseEnter={() => setOpenGroup(key)} onMouseLeave={() => setOpenGroup(null)}
                          onFocus={() => setOpenGroup(key)}
                          onBlur={(ev) => { if (!ev.currentTarget.contains(ev.relatedTarget)) setOpenGroup(null); }}
                          onKeyDown={(ev) => { if (ev.key === 'Escape') { ev.stopPropagation(); setOpenGroup(null); } }}>
                          <button type="button" className="as-pin as-pin-groupbtn" data-tone={tone} aria-expanded={isOpen}
                            aria-label={`${c.length} devices close together: ${summary}`}
                            onClick={() => setOpenGroup((g) => (g === key ? null : key))}>
                            <span className="as-pin-count">{c.length}</span>
                          </button>
                          {isOpen && (
                            <div className="as-pin-pop" role="group" aria-label="Devices in this group">
                              <p className="as-pin-pop-sum">{summary}</p>
                              <ul>
                                {devs.map((d) => {
                                  const st = pinStatus(d);
                                  return (
                                    <li key={d.id}>
                                      <Link to={`/assets/${d.id}`} data-testid="group-item">
                                        <span className="as-pin-badge" data-tone={st.tone} data-fixed={String(Boolean(d.fixed))} title={st.label}><CategoryIcon category={d.category_name} /></span>
                                        <strong>{catName(d)}</strong>
                                        {[d.manufacturer, d.model].filter(Boolean).length > 0 && ` · ${[d.manufacturer, d.model].filter(Boolean).join(' ')}`}
                                        {d.holder_name ? ` · ${holderLabel(d)}` : ''}
                                        {d.location_id !== placeId && d.location_name ? ` · ${d.location_name}` : ''}
                                      </Link>
                                    </li>
                                  );
                                })}
                              </ul>
                            </div>
                          )}
                        </span>
                      );
                    })}
                    {singles.map((d) => {
                      const at = toView(d.map_x, d.map_y);
                      const st = pinStatus(d);
                      return (
                        <button key={d.id} type="button" className="as-pin"
                          data-tone={st.tone} data-status={st.label} data-fixed={String(Boolean(d.fixed))}
                          data-hover={String(hover === d.id)}
                          style={{ left: pct(at.x), top: pct(at.y) }}
                          title={`${tagText(d)} · ${[d.category_name, d.manufacturer, d.model].filter(Boolean).join(' ')} · ${st.label}${d.location_id !== placeId ? ` · ${d.location_name}` : ''}`}
                          aria-label={`${tagText(d)}, ${st.label}${d.holder_name ? `, ${holderLabel(d)}` : ''}`}
                          onMouseEnter={() => setHover(d.id)} onMouseLeave={() => setHover(null)}
                          onMouseDown={startDrag('pin', d.id)}
                          data-selected={String(selected === d.id)}
                          onClick={(ev) => {
                            ev.stopPropagation();
                            // The first click shows the device in the list; a second opens it.
                            if (!moved.current) { if (selected === d.id) navigate(`/assets/${d.id}`); else selectDevice(d); }
                            moved.current = false;
                          }}>
                          <CategoryIcon category={d.category_name} />
                          <span className="as-pin-label">{catName(d)}</span>
                        </button>
                      );
                    })}
                    {deskDraw && (() => {
                      const a = toView(deskDraw.a.x, deskDraw.a.y);
                      const b = toView(deskDraw.b.x, deskDraw.b.y);
                      return (
                        <span className="as-desk-box as-desk-drawing" data-testid="desk-drawing"
                          style={{ left: pct(Math.min(a.x, b.x)), top: pct(Math.min(a.y, b.y)), width: pct(Math.abs(a.x - b.x)), height: pct(Math.abs(a.y - b.y)) }} />
                      );
                    })()}
                    {(data.desks || []).filter((k) => k.plan_location_id === planId && k.map_x !== null && k.map_x !== undefined)
                      .map((k) => ({ k, at: toView(k.map_x, k.map_y) })).filter(({ at }) => inView(at))
                      .map(({ k, at }) => (
                        <DeskPin key={k.id} desk={k} at={at} hover={hover} onHover={setHover}
                          onOpen={() => navigate(`/assets/desks/${k.id}`)} />
                      ))}
                  </div>
                ) : (
                  <div className="empty-state">
                    <p>{data.write ? 'No floor plan yet. Upload a PNG, JPEG or WebP picture of this place, or outline it on the plan of the floor above.' : 'No floor plan for this place yet.'}</p>
                  </div>
                )}
              </div>
              <div className="as-map-legend">
                {RING_LEGEND.map((r) => (
                  <span key={r.tone} className="as-map-legend-item">
                    <span className="as-pin-ring" data-tone={r.tone} aria-hidden="true" />{r.label}
                  </span>
                ))}
                <span className="as-map-legend-item" data-testid="legend-fixed">
                  <span className="as-pin-ring" data-tone="success" data-fixed="true" aria-hidden="true" />Fixed (stays put)
                </span>
                <span className="as-map-legend-item">
                  <span className="as-pin-ring" data-tone="success" aria-hidden="true" />Moves around
                </span>
                <span className="as-spacer" />
                <label className="as-map-legend-item">
                  <input type="checkbox" checked={showRetired} onChange={(e) => setShowRetired(e.target.checked)} />
                  Show retired
                </label>
              </div>
            </div>
            <div className="as-map-list">
              <h2>{`Devices here and beneath (${data.devices.length})`}</h2>
              {data.write && !isFloor && data.devices.length + deskKit > 0 && (
                <p>
                  <Link to={`/assets/labels?filter=1&location=${placeId}`} data-testid="place-print-labels">
                    {`Print labels for these ${data.devices.length + deskKit} device${data.devices.length + deskKit === 1 ? '' : 's'}`}
                  </Link>
                </p>
              )}
              {/* Gordon, 29 October: the room QR a stock take scans to say
                  where the counter is - this room's (and its desks'), or a
                  door label for every room on a floor. */}
              {data.write && !hasFloors && (
                <p>
                  <Link to={`/assets/places/${placeId}/labels`} data-testid="place-door-labels">
                    {isFloor ? 'Print door labels for every room (QR)' : "Print this room's label (QR)"}
                  </Link>
                </p>
              )}
              {allRoomKeys.length > 0 && (
                <div className="as-map-roomtools">
                  <button type="button" className="btn btn-plain btn-small" onClick={openAllRooms}>Open all</button>
                  <button type="button" className="btn btn-plain btn-small" onClick={foldAllRooms}>Fold all</button>
                </div>
              )}
              {data.devices.length === 0 && <p className="as-quiet">Nothing is kept at this place or the rooms beneath it.</p>}
              {(data.storage || []).length > 0 && (
                <section className="as-map-group" aria-label="Storage areas">
                  <h3>Storage areas</h3>
                  <ul className="as-tree">
                    {data.storage.map((s) => (
                      <li key={s.id} className="as-map-storeline">
                        <button type="button" className="btn btn-plain" onClick={() => setStoreOpen(s.id)}>
                          {`${s.name} · ${s.count} device${s.count === 1 ? '' : 's'}`}
                        </button>
                        {!labelled.has(s.id) && <span className="as-quiet">Label it on the plan to show its icon</span>}
                      </li>
                    ))}
                  </ul>
                </section>
              )}
              <DeskList desks={data.desks || []} placeId={placeId} onThisPlan={(k) => k.plan_location_id === planId}
                write={mapEdit} shown={shown} placing={placingDesk} hover={hover} onHover={setHover}
                onPlace={(id) => { setPlacing(null); setLabelling(null); setAddingDesk(null); setSizingDesk(null); setPlacingDesk(id); }}
                onUnpin={(id) => pinDesk(id, null)}
                sizing={data.desk_size_ready ? sizingDesk : undefined}
                onSize={(id) => { setPlacing(null); setLabelling(null); setAddingDesk(null); setPlacingDesk(null); setSizingDesk(id); }}
                onUnsize={(id) => sizeDesk(id, null)} />
              {data.devices.length > 0 && (
                <>
                  <section className="as-map-half" aria-label="Not on the plan yet">
                    <h3 className="as-map-halfhead">
                      <button type="button" className="as-map-halfbtn" aria-expanded={unpinnedOpen} onClick={() => setUnpinnedOpen((v) => !v)} data-testid="unpinned-head">
                        <span aria-hidden="true">{unpinnedOpen ? '▾ ' : '▸ '}</span>{`Not on the plan yet (${unpinnedList.length})`}
                      </button>
                    </h3>
                    {unpinnedOpen && unpinnedList.length === 0 && <p className="as-quiet">Every device here has its place on the plan.</p>}
                    {unpinnedOpen && renderGroups(unpinnedList)}
                  </section>
                  <section className="as-map-half" aria-label="On the plan">
                    <h3 className="as-map-halfhead">
                      <button type="button" className="as-map-halfbtn" aria-expanded={pinnedOpen} onClick={togglePinned} data-testid="pinned-head">
                        <span aria-hidden="true">{pinnedOpen ? '▾ ' : '▸ '}</span>{`On the plan (${pinnedList.length})`}
                      </button>
                    </h3>
                    {pinnedOpen && renderGroups(pinnedList)}
                  </section>
                </>
              )}
            </div>
          </div>
          )}
        </>
      )}
      {planAway && !showBuilding && shown && (
        <button type="button" className="btn btn-primary as-map-toplan"
          onClick={() => colRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>Show plan</button>
      )}
      {storeOpen && (
        <StoragePanel placeId={storeOpen} meta={meta} onClose={() => setStoreOpen(null)} onChanged={load} />
      )}
      {confirmDialog}
    </div>
  );
}
